// Case studies — real projects by Ankita Gogia.
// Visuals sourced from the client's original brand guidelines / mockups,
// rendered into /public/case-studies/<id>/.

const jpgs = (id, count) =>
  Array.from({ length: count }, (_, i) => `/case-studies/${id}/s${String(i + 1).padStart(2, "0")}.jpg`);

export const CASE_STUDIES = [
  {
    id: "unpopular-you",
    title: "Unpopular You",
    subtitle: "Skincare — Quiet Confidence",
    category: "Skincare",
    year: "2025",
    accent: "#A78C7B",
    cover: "/case-studies/unpopular-you/s01.jpg",
    hero: "/case-studies/unpopular-you/s01.jpg",
    summary:
      "Repositioned around 'Quiet Confidence' — a premium, launch-ready ecosystem for a melanin-rich, sensitive-skin brand.",
    tags: ["Brand Strategy", "Packaging Systems", "Visual Identity"],
    metrics: [
      { value: "6", label: "Signature tones" },
      { value: "8+", label: "Applications" },
      { value: "1", label: "Unified system" },
    ],
    problem:
      "Unpopular You had premium clinical formulations, but their public-facing branding used generic beauty tropes that blended into crowded digital shelves. The unpolished look diluted the founder's market authority.",
    strategy:
      "We skipped trendy, loud cosmetics design and carved out an uncontested space: 'Quiet Confidence.' An elegant, warm-toned palette — Vanilla Mist, Porcelain Mist and Cocoa Earth — was engineered to mirror and celebrate melanin skin undertones.",
    process:
      "•  Signature Identity — a responsive brand mark built for both tiny social icons and large retail storefronts.\n•  Bespoke Packaging — minimalist label architecture engineered for immediate shelf recognition.\n•  Global Launch Blueprint — an AI-assisted visual asset workflow that lets the team scale future product lines instantly.",
    solution:
      "A cohesive, launch-ready system spanning identity, packaging and a scalable AI-assisted content workflow — every touchpoint feels unmistakably unified.",
    outcome:
      "A unified, premium brand footprint that eliminated positioning friction, drove immediate restocks after launch, and built instant market trust.",
    gallery: jpgs("unpopular-you", 2),
  },
  {
    id: "nexist",
    title: "Nexist",
    subtitle: "Virtual CFO Website",
    category: "Web Design",
    year: "2026",
    accent: "#20397C",
    cover: "/case-studies/nexist/s01.jpg",
    hero: "/case-studies/nexist/s01.jpg",
    summary:
      "A conversion-focused website for a Virtual CFO firm that helps ambitious founders find hidden cash and scale without burnout.",
    tags: ["Web Design", "Creative Direction", "Landing Page"],
    metrics: [
      { value: "$600K", label: "Client profit found" },
      { value: "25 hrs/wk", label: "Owner time reclaimed" },
      { value: "$100K", label: "Roadmap promise" },
    ],
    problem:
      "Nexist offers high-value financial and operations expertise, but founders were 'flying blind' and needed a site that made a complex service instantly understandable — and compelling enough to book a call.",
    strategy:
      "I led with a founder's real pain: 'Stop working 60+ hour weeks for a bank balance that doesn't move.' The narrative moves from problem (cash leaks, single point of failure) to the Finance-First framework and a clear, low-friction CTA — the free Business Scorecard.",
    process:
      "Using a confident deep-teal (#20397C) with bright cyan, magenta and gold accents, I structured a story-driven page: hero + trust badges, problem cards, the VCFO framework diagram, social proof, and a WhatsApp 'quick question' shortcut for instant contact.",
    solution:
      "A cohesive, high-trust marketing site with strong hierarchy, credibility markers and multiple conversion paths — built to turn skeptical, time-poor founders into booked discovery calls.",
    outcome:
      "The page makes an abstract service tangible, headlined by a real client result — $600K found in lost profits and 25 hrs/week reclaimed — to drive scorecard sign-ups.",
    gallery: jpgs("nexist", 4),
  },
  {
    id: "neha-cfo",
    title: "Neha Malhotra",
    subtitle: "CFO Personal Brand Website",
    category: "Web Design",
    year: "2026",
    accent: "#20397C",
    cover: "/case-studies/neha-cfo/s01.jpg",
    hero: "/case-studies/neha-cfo/s01.jpg",
    summary:
      "A premium personal-brand website for an award-winning CFO — positioning her as a strategic business fixer, not just an accountant.",
    tags: ["Web Design", "Personal Branding", "Creative Direction"],
    metrics: [
      { value: "40U40", label: "Award winner" },
      { value: "FCPA", label: "Certified" },
      { value: "20+", label: "Services mapped" },
    ],
    problem:
      "Neha, an FCPA and CFO, needed a personal brand that elevated her above 'accountant' — communicating strategic authority and helping founders move from being busy to building real wealth.",
    strategy:
      "I built the site around a memorable promise: 'Numbers tell a story. I'm here to make sure yours has a profitable ending.' The narrative pairs credibility (40 Under 40, awards) with a warm, human, results-first voice.",
    process:
      "A confident palette of vibrant pink, deep navy and clean neutrals, with an editorial serif headline, gives the brand warmth and gravitas. Sections flow from hero and 'Beyond the Balance Sheet' story to awards, a founder video, practical tips and clear CTAs.",
    solution:
      "A polished, high-trust personal website that showcases her breadth — from Virtual CFO and forecasting to systems, SOPs and AI strategy — while feeling personable and premium.",
    outcome:
      "A distinctive CFO personal brand backed by real credibility markers (40 Under 40, Business Elite Top 40) that turns expertise into booked strategy calls.",
    gallery: jpgs("neha-cfo", 2),
  },
  {
    id: "suttons-bakery",
    title: "Suttons Bakery",
    subtitle: "Heritage Bakery Website",
    category: "Web Design",
    year: "2026",
    accent: "#7A1F2A",
    cover: "/case-studies/suttons-bakery/s01.jpg",
    hero: "/case-studies/suttons-bakery/s01.jpg",
    summary:
      "A warm, heritage-led website for Australia's second-oldest bakery — honouring 160+ years of craft while driving online orders.",
    tags: ["Web Design", "Creative Direction", "Art Direction"],
    metrics: [
      { value: "1864", label: "Baking since" },
      { value: "2nd", label: "Oldest in Australia" },
      { value: "3", label: "Signature bakes" },
    ],
    problem:
      "Suttons Bakery in Echuca is the second-oldest bakery in Australia, but needed a modern website that could honour its 1864 legacy while making it effortless to order online and visit in store.",
    strategy:
      "I leaned into heritage and craft — 'Echuca's Bread Legacy' — building a warm, artisanal, trustworthy experience that celebrates tradition while guiding visitors to order or drop by.",
    process:
      "A rich burgundy, cream and gold palette with editorial headlines sets a heritage tone. Sections flow from a hero story to 'Echuca Heritage Bakery', 'Artisan Creations' (Traditional Sourdough, Vienna Loaf, Vanilla Slice), a 'Fresh to Your Door' Uber Eats module, and a visit-us block with hours and contact.",
    solution:
      "A homepage that balances storytelling with commerce — showcasing signature bakes, enabling delivery, and inviting locals and visitors to experience the bakery in person.",
    outcome:
      "A warm, credible digital home that turns more than a century and a half of legacy into online orders and foot traffic.",
    gallery: jpgs("suttons-bakery", 3),
  },
  {
    id: "four-square",
    title: "Four Square Cabinets",
    subtitle: "E-commerce Website Design",
    category: "Web Design",
    year: "2026",
    accent: "#7b7061",
    cover: "/case-studies/four-square/s01.jpg",
    hero: "/case-studies/four-square/s01.jpg",
    summary:
      "A warm, premium homepage for a cabinetry brand offering affordable luxury — from expert design to flawless installation.",
    tags: ["Web Design", "E-commerce", "Art Direction"],
    metrics: [
      { value: "4.7★", label: "Google rating" },
      { value: "5-yr", label: "Warranty promise" },
      { value: "7", label: "Product ranges" },
    ],
    problem:
      "Four Square Cabinets needed a homepage that felt like 'the heart of a modern home' — premium enough to justify craftsmanship, yet simple enough to shop products and request a quote.",
    strategy:
      "I positioned the brand around affordable luxury and lifestyle, pairing aspirational interior photography with a clear commercial path: explore products, browse solutions by space, and book a showroom visit.",
    process:
      "A warm neutral palette (#EDE9E0, #D5C9B1, #7B7061) with an amber CTA keeps the experience calm and tactile. I organised the page into hero, product range, solutions-by-space, what-makes-us-unique, Google-backed testimonials, a work gallery and a contact/booking flow.",
    solution:
      "A conversion-ready homepage that balances editorial warmth with e-commerce clarity — showcasing MDF boards, cladding, fireplaces, partitions and more, each with pricing and add-to-cart.",
    outcome:
      "A trustworthy, lifestyle-led storefront reinforced by a 4.7★ Google rating and a 5-year warranty, designed to convert browsers into quote requests and showroom visits.",
    gallery: jpgs("four-square", 7),
  },
  {
    id: "real-estate-social",
    title: "Gogia Properties",
    subtitle: "Real Estate Social Media",
    category: "Social Media Design",
    year: "2023",
    accent: "#C8A96A",
    cover: "/case-studies/real-estate-social/s01.jpg",
    hero: "/case-studies/real-estate-social/s01.jpg",
    summary:
      "A polished, gold-accented social media creative system for a real estate and interiors brand — built to attract dream-home buyers.",
    tags: ["Social Media Design", "Creative Direction", "Templates"],
    metrics: [
      { value: "12+", label: "Post templates" },
      { value: "2", label: "Campaign themes" },
      { value: "1", label: "Unified system" },
    ],
    problem:
      "Gogia Properties needed a social presence that felt as premium as the homes it sells — consistent, luxurious and instantly recognisable across property listings and interior-design content.",
    strategy:
      "I built a warm, gold-and-cream creative system that positions the brand around aspiration and trust — 'your dream, our mission' — with clear calls to action and contact details baked into every post.",
    process:
      "Using an elegant gold, brown and cream palette with strong editorial headlines and rounded image framing, I designed two coordinated content pillars — real-estate investment and timeless interiors — as reusable, on-brand templates.",
    solution:
      "A cohesive library of feed creatives — 'Secure Your Dream Home', 'Timeless Interiors', 'The Key to Your Dream House', 'Open the Door to Real Estate Investment' and more — each carrying consistent branding and contact info.",
    outcome:
      "A recognisable, premium social identity that lets Gogia Properties post consistently and credibly, turning scrollers into enquiries.",
    gallery: [
      "/case-studies/real-estate-social/s01.jpg",
      "/case-studies/real-estate-social/s02.jpg",
    ],
  },
  {
    id: "purifyroots",
    title: "Purifyroots",
    subtitle: "Wellness & Supplement Brand",
    category: "Visual Identity",
    year: "2024",
    accent: "#247761",
    cover: "/case-studies/purifyroots/s01.jpg",
    hero: "/case-studies/purifyroots/s01.jpg",
    summary:
      "A fresh, natural identity for a herbal wellness and supplement brand — clean, energetic and trustworthy across pack and digital.",
    tags: ["Brand Strategy", "Visual Identity", "Packaging"],
    metrics: [
      { value: "4", label: "Brand colours" },
      { value: "2", label: "Hero products" },
      { value: "8+", label: "Applications" },
    ],
    problem:
      "Purifyroots offers natural supplements like Ashwagandha and its Pranexis energy enhancer, but needed an identity that felt clean, credible and vibrant enough to compete on shelf in the wellness category.",
    strategy:
      "I positioned Purifyroots around vitality and purity — nature-powered energy you can trust. The system had to feel fresh and modern while staying warm and approachable across packaging, apparel and app.",
    process:
      "A rounded, friendly logotype with a leaf-drop mark, paired with the Croogla headline and Glacial Indifference body fonts, sits on a bright, natural palette — #247761 Deep Green, #60B63B Fresh Green, #F5E300 Vital Yellow and #E6FFDB Soft Mint.",
    solution:
      "A cohesive identity applied to supplement packaging (Pranexis pouch, Ashwagandha bottle), a logo sticker, sweatshirt, patterned textiles, a mobile app splash and a tote — a complete, energetic brand world.",
    outcome:
      "A confident, natural-first supplement brand with a distinctive green-and-yellow system that reads instantly as fresh, healthy and trustworthy.",
    gallery: ["/case-studies/purifyroots/s01.jpg"],
  },
  {
    id: "brio-and-bean",
    title: "Brio & Bean",
    subtitle: "Coffee Brand & Packaging",
    category: "Visual Identity",
    year: "2021",
    accent: "#602B02",
    cover: "/case-studies/brio-and-bean/s01.jpg",
    hero: "/case-studies/brio-and-bean/s01.jpg",
    summary:
      "A complete brand identity for a specialty coffee house — warm, energetic and full of character, from logo to packaging.",
    tags: ["Brand Strategy", "Visual Identity", "Packaging"],
    metrics: [
      { value: "2", label: "Signature palettes" },
      { value: "8+", label: "Branded applications" },
      { value: "1", label: "Custom wordmark" },
    ],
    problem:
      "Brio & Bean needed an identity that could stand out in a crowded café market and communicate quality and experience at a glance — a brand crafted for coffee lovers who value both the cup and the ritual around it.",
    strategy:
      "I anchored the brand in two ideas: energy and authenticity. The positioning captures the boldness of espresso and the smooth comfort of coffee — warm, inviting and full of character — so every touchpoint feels premium yet approachable.",
    process:
      "I designed a rounded, confident wordmark with a signature 'dripping' detail that signals a fresh pour, then built two coordinated palettes — a Rich Roast palette (#602B02 Espresso Brown, #E6CEA4 Creamy Latte, #D4A759 Golden Caramel) and a Fresh Essence palette (Cool Sage, #F3E5C0 Soft Sand, #D17C36 Amber Spice) — for depth and versatility.",
    solution:
      "A full brand system applied across menu, coffee-bag packaging, to-go and branded cups, shop and hanging signage, business cards, apparel and digital menu mockups — a cohesive, shelf-ready identity.",
    outcome:
      "The result is a warm, ownable coffee brand with a flexible dual-palette system that scales confidently from a single cup to a full storefront experience.",
    gallery: jpgs("brio-and-bean", 13),
  },
  {
    id: "veyora",
    title: "Veyora",
    subtitle: "Organic Tea & Spices Brand",
    category: "Visual Identity",
    year: "2022",
    accent: "#1F4F45",
    cover: "/case-studies/veyora/s01.jpg",
    hero: "/case-studies/veyora/s01.jpg",
    summary:
      "A refined, nature-led identity for an organic tea and spices brand — pure, earthy and quietly premium.",
    tags: ["Brand Strategy", "Visual Identity", "Packaging"],
    metrics: [
      { value: "1", label: "Leaf monogram" },
      { value: "3", label: "Signature tones" },
      { value: "6+", label: "Applications" },
    ],
    problem:
      "Veyora needed a brand that could communicate purity and 'nature's finest flavours' — organic tea and spices that feel carefully cultivated and genuinely premium, not mass-market.",
    strategy:
      "I built the identity around 'Pure Essence — purity in every sip', leaning into an earthy, botanical world that signals authenticity and calm, refined quality.",
    process:
      "The Veyora wordmark integrates a growing leaf into the 'o' to symbolise nature and growth, set in the elegant Tan Pearl typeface with Montserrat for support. A deep forest-green and lime palette, plus a bespoke leaf-pattern system, carries the brand across every surface.",
    solution:
      "A complete identity — logo variations for dark and light, an iconography/pattern sheet, tea-bag packaging, storefront signage, gift packaging and lifestyle art direction rooted in nature.",
    outcome:
      "A serene, ownable organic-tea brand whose leaf motif and earthy palette make purity and provenance feel tangible at every touchpoint.",
    gallery: jpgs("veyora", 6),
  },
];

export const getCaseStudy = (id) => CASE_STUDIES.find((c) => c.id === id);

// Mobile variant path helper (e.g. /x/s01.jpg -> /x/s01-sm.jpg)
export const smSrc = (p) => (p && p.endsWith(".jpg") ? p.replace(/\.jpg$/, "-sm.jpg") : p);
